import React from 'react';
import { Bell, BellOff } from 'lucide-react';

import type {
  MerchantReportRiskLevel,
  MerchantReportType,
  NormalizedMcc,
  NormalizedMerchantDetails,
  PolicyFitStatus,
} from '@ballerine/common';

import { ctw } from '@/common';
import { Card, CopyButton } from '@/components';
import { Separator } from '@/components/atoms/Separator';
import {
  QuestionMarkTooltip,
  RiskLevelHexagon,
  RiskTypesDisplay,
} from '../../report-shared/components';
import { GoogleMapsPin } from '../components/GoogleMapsPin/GoogleMapsPin';

type HeaderSectionProps = {
  reportId?: string;
  homepageScreenshotUrl?: string | null;
  riskLevel?: MerchantReportRiskLevel | null;
  lineOfBusiness?: string | null;
  mcc?: NormalizedMcc | null;
  geographies?: string[] | null;
  riskTypes?: Record<string, { status: 'clear' | 'alert' } | null> | null;
  policyFit?: {
    status?: PolicyFitStatus;
    description?: string | null;
  } | null;
  isPolicyFitEnabled?: boolean;
  isMonitoringReport: boolean;
  isAlert?: boolean;
  FeedbackComponent?: React.ComponentType<{ reportId: string }>;
  reportType?: MerchantReportType | string;
  merchantDetails?: NormalizedMerchantDetails | null;
  googleMapsApiKey?: string;
};

const GeneralInfoSection: React.FC<{
  lineOfBusiness?: string | null;
  mccText: string;
  className?: string;
}> = ({ lineOfBusiness, mccText, className }) => (
  <div className={ctw('py-6', className)}>
    <div className="mb-3 flex items-center gap-1">
      <h4 className="text-sm font-semibold text-stone-400">GENERAL INFO</h4>
      <QuestionMarkTooltip
        text="Basic merchant information including business type, merchant category code, and geographical scope."
        className="ml-2"
      />
    </div>
    <div className="space-y-2 text-sm">
      {lineOfBusiness && (
        <div>
          <span className="font-bold text-slate-900">Line Of Business</span>
          <br />
          <span className="text-black">{lineOfBusiness}</span>
        </div>
      )}
      {mccText && (
        <div>
          <span className="font-bold text-slate-900">MCC</span>
          <br />
          <div>
            <span className="text-black">{mccText}</span>
            <CopyButton className="ml-2 inline-flex" textToCopy={mccText} />
          </div>
        </div>
      )}
    </div>
  </div>
);

export const HeaderSection: React.FC<HeaderSectionProps> = ({
  reportId,
  riskLevel,
  lineOfBusiness,
  mcc,
  riskTypes,
  reportType,
  FeedbackComponent,
  merchantDetails,
  googleMapsApiKey,
  isMonitoringReport,
  isAlert = false,
}) => {
  const mccText = [mcc?.code, mcc?.category].filter(Boolean).join(' - ');
  const showHeaderMap = merchantDetails?.address && googleMapsApiKey;
  const isPosReport = reportType === 'POINT_OF_SALE_REPORT';
  const hasRiskTypes = isPosReport && riskTypes && Object.keys(riskTypes).length > 0;

  return (
    <Card className="mb-6 overflow-hidden rounded-3xl bg-white">
      <div className="flex flex-col">
        {showHeaderMap && (
          <div className="relative h-80 bg-gray-100">
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100">
              <GoogleMapsPin
                address={merchantDetails.address}
                companyName={merchantDetails.companyName}
                countryCode={merchantDetails.country}
                googleMapsApiKey={googleMapsApiKey}
              />
            </div>
            {isMonitoringReport && (
              <div className="absolute bottom-6 left-6 z-10 text-sm">
                <div className="z-10 flex items-center gap-2 rounded bg-white px-2 py-1 text-sm font-medium shadow-lg transition-colors hover:bg-gray-50">
                  {isAlert ? (
                    <>
                      <div className="relative">
                        <Bell className="h-4 w-4 text-neutral-900" />
                        <div className="absolute right-[1.5px] top-[1.5px] h-[6px] w-[6px] rounded-full border border-white bg-red-400" />
                      </div>
                      <span className="text-sm text-neutral-500">Changes Detected</span>
                    </>
                  ) : (
                    <>
                      <BellOff className="h-4 w-4 text-neutral-900" />
                      <span className="text-sm text-neutral-500">No Changes</span>
                    </>
                  )}
                </div>
              </div>
            )}
            {FeedbackComponent && reportId ? (
              <div className="absolute bottom-4 right-4 z-10">
                <FeedbackComponent reportId={reportId} />
              </div>
            ) : (
              <a
                href={`mailto:support@ballerine.com?subject=${encodeURIComponent(`Feedback for Report ${reportId || ''}`)}&body=${encodeURIComponent(`Report ID: ${reportId || 'Unknown'}\n\nFeedback:\n`)}`}
                className="absolute bottom-4 right-4 z-10 rounded border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-zinc-950 transition-colors hover:bg-gray-50"
              >
                Provide Feedback
              </a>
            )}
          </div>
        )}

        <div className="flex gap-6 bg-white">
          <div className="flex flex-1">
            <div
              className={ctw(
                `flex min-w-48 flex-none flex-col items-center gap-2 py-6 text-center`,
                hasRiskTypes ? 'w-1/3' : 'w-auto',
              )}
            >
              <h4 className="text-center text-sm font-semibold">RISK LEVEL</h4>
              <RiskLevelHexagon riskLevel={riskLevel} />
            </div>

            <Separator orientation="vertical" />

            {hasRiskTypes && (
              <div className="w-2/3 flex-none py-6 pl-8">
                <div className="mb-3 flex items-center gap-1">
                  <h4 className="text-sm font-semibold text-stone-400">RISK TYPES</h4>
                  <QuestionMarkTooltip
                    text="Specific risk categories assessed including chargebacks, reputation, and legal considerations."
                    className="ml-2"
                  />
                </div>
                <RiskTypesDisplay
                  riskTypes={riskTypes}
                  allowedKeys={['chargebacks', 'reputational', 'legal']}
                />
              </div>
            )}

            {!hasRiskTypes && (
              <GeneralInfoSection
                lineOfBusiness={lineOfBusiness}
                mccText={mccText}
                className="flex-1 pl-8"
              />
            )}
          </div>

          {hasRiskTypes && (
            <div className="flex">
              <Separator orientation="vertical" />
            </div>
          )}

          {hasRiskTypes && (
            <GeneralInfoSection
              lineOfBusiness={lineOfBusiness}
              mccText={mccText}
              className="flex-1"
            />
          )}
        </div>
      </div>
    </Card>
  );
};
